import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import { AvailabilitySubstitutionRow, CatalogueGovernanceClient } from '../../../../app/services/api/catalogueGovernance';
import ApprovalStatusChip from './ApprovalStatusChip';
import RowActionsMenu from './RowActionsMenu';

const SUBSTITUTION_TYPE_KEY: Record<string, string> = {
  LikeForLike: 'LikeForLike',
  AlternativeBrand: 'AlternativeBrand',
  RecipeChangeRequired: 'RecipeChangeRequired',
};

const AvailabilitySubstitutionsTab = () => {
  const { t } = useTranslation('catalogueGovernance');
  const [rows, setRows] = useState<AvailabilitySubstitutionRow[]>([]);
  const [notifiedIds, setNotifiedIds] = useState<Set<string>>(new Set());

  const load = () => CatalogueGovernanceClient.getAvailabilitySubstitutions().then(setRows);

  useEffect(() => {
    load();
  }, []);

  const handleAction = async (action: (id: string) => Promise<void>, id: string) => {
    await action(id);
    load();
  };

  const handleNotify = async (id: string) => {
    await CatalogueGovernanceClient.notifySites(id);
    setNotifiedIds((prev) => new Set(prev).add(id));
  };

  return (
    <Section>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('availabilitySubstitutions.columnSupplier', 'Supplier')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnProduct', 'Product')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnCatalogue', 'Catalogue')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnAvailabilityStatus', 'Availability status')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnUnavailableFrom', 'Unavailable from')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnExpectedReturn', 'Expected return')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnAffectedSites', 'Affected sites')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnApprovedSubstitute', 'Approved substitute')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnSubstitutionType', 'Substitution type')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnApprovalStatus', 'Approval status')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnAllergenImpact', 'Allergen impact')}</TableCell>
            <TableCell align="right">{t('availabilitySubstitutions.columnCostImpact', 'Cost impact')}</TableCell>
            <TableCell>{t('availabilitySubstitutions.columnActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={13}>{t('availabilitySubstitutions.emptyState', 'No availability issues to show.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.product}</TableCell>
              <TableCell>{row.catalogue}</TableCell>
              <TableCell><ApprovalStatusChip status={row.availabilityStatus} /></TableCell>
              <TableCell>{row.unavailableFrom}</TableCell>
              <TableCell>{row.expectedReturnDate ?? '-'}</TableCell>
              <TableCell>{row.affectedSites}</TableCell>
              <TableCell>{row.approvedSubstitute ?? '-'}</TableCell>
              <TableCell>{row.substitutionType
                ? t(`availabilitySubstitutions.substitutionType.${SUBSTITUTION_TYPE_KEY[row.substitutionType]}`, row.substitutionType)
                : '-'}</TableCell>
              <TableCell><ApprovalStatusChip status={row.approvalStatus} /></TableCell>
              <TableCell>{row.allergenImpact}</TableCell>
              <TableCell align="right">{row.costImpact > 0 ? '+' : ''}£{row.costImpact.toFixed(2)}</TableCell>
              <TableCell>
                <RowActionsMenu actions={[
                  {
                    label: t('availabilitySubstitutions.actionMarkUnavailable', 'Mark unavailable'),
                    disabled: row.availabilityStatus === 'Unavailable',
                    onClick: () => handleAction(CatalogueGovernanceClient.markProductUnavailable.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('availabilitySubstitutions.actionApproveSubstitute', 'Approve substitute'),
                    disabled: !row.approvedSubstitute || row.approvalStatus === 'Approved',
                    onClick: () => handleAction(CatalogueGovernanceClient.approveSubstitute.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('availabilitySubstitutions.actionRejectSubstitute', 'Reject substitute'),
                    disabled: !row.approvedSubstitute || row.approvalStatus === 'Rejected',
                    destructive: true,
                    onClick: () => handleAction(CatalogueGovernanceClient.rejectSubstitute.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: notifiedIds.has(row.id)
                      ? t('availabilitySubstitutions.actionSitesNotified', 'Sites notified')
                      : t('availabilitySubstitutions.actionNotifySites', 'Notify sites'),
                    disabled: notifiedIds.has(row.id),
                    onClick: () => handleNotify(row.id),
                  },
                ]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

export default AvailabilitySubstitutionsTab;
